import type {
  BulkDeleteResult,
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import { SearchBaseDto } from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateDto,
  UpdateDto,
} from 'switch-shared/modules/crud/base/base.service';
import {
  SearchZoneOutput,
  ZoneHierarchyResult,
} from 'switch-shared/modules/crud/zone/zone.dto';
import { Zone } from 'switch-shared/modules/crud/zone/zone.entity';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS } from './config/apiTags';

export const utilityAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [ENDPOINT_URLS.ZONE],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getZoneById: builder.query<Zone, string>({
        query: (id) => `${ENDPOINT_URLS.ZONE}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.ZONE, id }],
      }),

      getAllZones: builder.query<SearchZoneOutput, SearchBaseDto>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.ZONE}`,
            method: API_METHODS.GET,
            params,
          };
        },
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.ZONE,
            id: con._id,
          })) ?? [],
      }),

      createZone: builder.mutation<Zone, CreateDto<Zone>>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.ZONE}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.ZONE],
      }),

      updateZone: builder.mutation<Zone, { id: string; data: UpdateDto<Zone> }>(
        {
          query: ({ id, data }) => ({
            url: `${ENDPOINT_URLS.ZONE}/${id}`,
            method: API_METHODS.PATCH,
            body: data,
          }),
          invalidatesTags: (_res, _err, { id }) => [
            {
              type: ENDPOINT_URLS.ZONE,
              id,
            },
          ],
        }
      ),

      bulkRenameZones: builder.mutation<BulkRenameResult[], BulkRenameItem[]>({
        query: (items) => ({
          url: `${ENDPOINT_URLS.ZONE}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) =>
          items.map(({ id }) => ({ type: ENDPOINT_URLS.ZONE, id })),
      }),

      bulkDeleteZone: builder.mutation<BulkDeleteResult[], string[]>({
        query: (ids) => ({
          url: `${ENDPOINT_URLS.ZONE}`,
          method: API_METHODS.DELETE,
          body: ids,
        }),
        invalidatesTags: [ENDPOINT_URLS.ZONE],
      }),

      getZoneHierarchy: builder.query<
        ZoneHierarchyResult,
        {
          applicationId: string;
          rootZoneId?: string;
          includeMeterPoints?: boolean;
        }
      >({
        query: ({ applicationId, rootZoneId, includeMeterPoints }) => ({
          url: `${ENDPOINT_URLS.ZONE}/hierarchy/${applicationId}`,
          method: API_METHODS.GET,
          params: { rootZoneId, includeMeterPoints },
        }),
        providesTags: [ENDPOINT_URLS.ZONE],
      }),
    }),
  });

export const {
  useGetZoneByIdQuery,
  useGetAllZonesQuery,
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useBulkRenameZonesMutation,
  useBulkDeleteZoneMutation,
  useGetZoneHierarchyQuery,
} = utilityAPI;

setToast('bulkDeleteZone', { bulkResults: { noun: 'zone', verb: 'delete' } });
setToast('bulkRenameZones', { bulkResults: { noun: 'zone', verb: 'rename' } });
